<template>

    <kt-modal
        :visible="recordVisible"
        :title="recordTitle"
        @dismiss="onDismiss()"
        size="modal-lg"
    >
        <template #body>
            <div v-if="jobInfo?.Id" class="card mb-3">
                <div class="card-body py-2 px-3 d-flex align-items-center justify-content-between">
                    <div>
                        <small class="text-muted">Job</small><br />
                        <strong>
                            <span class="text-muted">ID{{ jobInfo.Id }}</span>
                            {{ jobInfo.Contact?.Code }}
                            <span v-if="jobInfo.Contact?.Code && jobLabelDescription"> - </span>
                            {{ jobLabelDescription }}
                        </strong>
                    </div>
                    <button
                        class="btn btn-sm btn-outline-secondary"
                        @click="editRequestTicket()"
                        title="Edit Request"
                    >
                        <Edit :size="16" />
                    </button>
                </div>
            </div>

            <part-form v-model="record"></part-form>

            <!--
            // moving to the partform ...
            Show attachments section only if part is saved (has an ID)
            <div v-if="record.Id" class="mt-3">
                <part-attachment-form
                    :part-id="record.Id"
                    :request-id="record.RequestId"
                ></part-attachment-form>
            </div>
            -->
        </template>
    
        <template #footer>
            <div class="kt-modal-actions d-flex align-items-center justify-content-between w-100">
                <div class="form-check form-switch">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="sendNotificationTogglePart"
                        v-model="sendNotification"
                    >
                    <label class="form-check-label" for="sendNotificationTogglePart">
                        Send Update Notification
                    </label>
                </div>
                <div>
                    <button @click="onDismiss()" class="btn btn-outline-secondary">
                        <X :size="16" class="me-1" />Close
                    </button>
                    <button v-if="record.Id" @click="deleteRecord()" class="btn btn-outline-danger ms-2">
                        <Trash2 :size="16" class="me-1" />Delete
                    </button>
                    <button @click="saveRecord()" class="btn btn-primary ms-2">
                        <Save :size="16" class="me-1" />Save
                    </button>
                </div>
            </div>
        </template>
        
    </kt-modal>

    <!-- Edit Request Ticket Modal (mirrors the one in JobAc.vue) -->
    <kt-modal
        :title="requestEditTitle"
        :visible="requestEditVisible"
        :size="'modal-xl'"
        @dismiss="requestEditVisible = false"
    >
        <template #body>
            <request-form v-model="requestForm"></request-form>
        </template>

        <template #footer>
            <div class="kt-modal-actions d-flex justify-content-between w-100">
                <button @click="requestEditVisible = false" class="btn btn-outline-secondary">
                    <X :size="16" class="me-1" />Close
                </button>
                <button @click="saveRequestTicket()" class="btn btn-primary">
                    <Save :size="16" class="me-1" />Save
                </button>
            </div>
        </template>
    </kt-modal>

</template>

<script lang="ts" setup>

import { ref, computed, watch, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { PartModel } from "@/models/PartModel";
import { WorkOrderModel } from "@/models/WorkOrderModel";
import ApiService from "@/services/ApiService";
import Emitter from "@/services/Emitter";
import Helper from "@/services/Helper";
import Alert from "@/services/Alert";
import Toast from "@/services/Toast";
import KtModal from "@/components/UI/KtModal.vue";
import PartForm from "@/components/Parts/PartForm.vue";
import RequestForm from "@/components/Requests/RequestForm.vue";
import PartAttachmentForm from "@/components/Parts/PartAttachmentForm.vue";
import { X, Trash2, Save, Edit } from 'lucide-vue-next';

const route = useRoute();
const router = useRouter();
const recordVisible = ref(true);

// Loaded parent Request (Job/umbrella) info, used for the modal title and
// the header card above the PartForm.
const jobInfo = ref<any>(null);

const jobLabelDescription = computed(() =>
    jobInfo.value?.RequestDetailedDescription || jobInfo.value?.TaskDetail || ""
);

const recordTitle = computed(() => {
    if (!jobInfo.value?.Id) return "Part Entry";
    const parts = ["Part Entry"];
    parts.push(`ID${jobInfo.value.Id}`);
    if (jobInfo.value.Contact?.Code) parts.push(jobInfo.value.Contact.Code);
    if (jobLabelDescription.value) parts.push(`- ${jobLabelDescription.value}`);
    return parts.join(" ");
});

// State for the inline request-ticket edit modal.
const requestEditVisible = ref(false);
const requestEditTitle = ref("Edit Request");
const requestForm = ref<WorkOrderModel>(new WorkOrderModel());

const props = defineProps({
    id: {
        type: [Number, String],
        default: null
    },
    requestId: {
        type: Number,
        default: 0
    },
    // The WorkOrder(Title='Request') ticket picked via JobAc - maps onto
    // PartModel.InitialRequest (RequestId is the Job/Request umbrella;
    // WorkOrderId is reserved for "this part is linked to an Invoice").
    workOrderId: {
        type: [Number, String],
        default: null
    },
    clientId: {
        type: Number,
        default: 0
    },
    techId: {
        type: Number,
        default: 0
    },
    embedded: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(["cancel", "saved"]);

const unwatch:Array <Function> = [];

const newRecord = () =>
{
    const rec = new PartModel();
    // Pre-populate with context if provided
    if (props.requestId) {
        rec.RequestId = props.requestId;
    }
    if (props.workOrderId) {
        rec.InitialRequest = Number(props.workOrderId);
    }
    if (props.clientId) {
        rec.CustomerNumber = props.clientId;
    }
    if (props.techId) {
        rec.AssignedTo = props.techId;
    }
    // Set default NeededBy and WorkCompleteDate to today
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${day}`;
    rec.NeededBy = todayStr;
    rec.WorkCompleteDate = todayStr;
    return rec;
};

const record = ref<PartModel>(newRecord());
const sendNotification = ref(false);

const loadJobInfo = async () => {
    // The parent Job may not be supplied when the form is opened from a
    // running clock where the task only carries the child WorkOrder ticket.
    // In that case, resolve the parent Job from the child ticket first.
    if (!record.value?.RequestId) {
        const childId = record.value?.InitialRequest || props.workOrderId;
        if (childId) {
            try {
                const ticket = await ApiService.loadWorkOrder(Number(childId));
                if (ticket?.RequestId && !record.value?.RequestId) {
                    record.value.RequestId = Number(ticket.RequestId);
                    // The watcher on record.RequestId will re-invoke loadJobInfo
                    // with the resolved parent Job id.
                    return;
                }
            } catch (error) {
                console.error("Failed to resolve parent job from work order:", error);
            }
        }

        if (!record.value?.RequestId) {
            jobInfo.value = null;
            return;
        }
    }

    try {
        jobInfo.value = await ApiService.loadRequest(record.value.RequestId);
    } catch (error) {
        console.error("Failed to load job info for part form:", error);
        jobInfo.value = null;
    }
};

const ticketIdToEdit = computed(() =>
    record.value?.InitialRequest || props.workOrderId || null
);

const editRequestTicket = async () => {
    const ticketId = ticketIdToEdit.value;
    if (!ticketId) {
        Alert.notify("No request ticket is linked to this part yet");
        return;
    }
    try {
        requestForm.value = await ApiService.loadWorkOrder(ticketId);
        requestEditTitle.value = `Edit Request #${ticketId}`;
        requestEditVisible.value = true;
    } catch (error) {
        console.error("Failed to load request ticket:", error);
        Alert.notify("Failed to load the request ticket");
    }
};

const saveRequestTicket = async () => {
    try {
        await ApiService.saveWorkOrder(requestForm.value);
        Toast.success("Request saved");
        requestEditVisible.value = false;
        await loadJobInfo();
    } catch (error) {
        console.error("Failed to save request ticket:", error);
        Alert.notify("Failed to save the request ticket");
    }
};

const saveRecord = async () =>
{
    try {
        let data = Helper.prep(record.value);
        
        // .. TODO ... validate record here


        console.debug("save", data);

        data = await ApiService.savePart(data);

        // If embedded mode or global modal mode (any context props provided), emit saved event
        const isGlobalModal = props.id !== null || props.requestId || props.workOrderId || props.clientId || props.techId;
        if (props.embedded || isGlobalModal) {
            emit("saved");
        } else {
            // redirect
            Emitter.emit("redirect", "/part/edit/" + data.Id);
        }
    } catch (error) {
        console.error("Save failed:", error);
    }
};

const deleteRecord = async () =>
{
    Alert.notify("Deletion is currently disabled.");
};

const onDismiss = () =>
{
    // If embedded mode or global modal mode (any context props provided), emit cancel event
    const isGlobalModal = props.id !== null || props.requestId || props.workOrderId || props.clientId || props.techId;
    if (props.embedded || isGlobalModal) {
        emit("cancel");
    } else {
        router.back();
    }
};

const loadRecord = async (val?: string | number | null) =>
{
    // Priority: id prop > embedded mode > route params
    if (props.id !== null && props.id !== undefined) {
        // Global modal mode with id prop
        if (props.id && props.id !== "new") {
            let rec = await ApiService.loadPart(props.id);
            console.debug("part", rec);
            record.value = rec;
        } else {
            record.value = newRecord();
        }
        await loadJobInfo();
    } else if (props.embedded && props.requestId) {
        // In embedded mode, create a new part for the request
        // (newRecord() already applies props.requestId/workOrderId)
        record.value = newRecord();
        await loadJobInfo();
    } else if (val) {
        // Route-based loading
        switch (val.toString())
        {
            case "new":
                record.value = newRecord();
                break;

            default:
                let rec = await ApiService.loadPart(val);
                console.debug("part", rec);
                record.value = rec;
                break;
        }
        await loadJobInfo();
    } else {
        record.value = newRecord();
    }
    await loadJobInfo();
};

// Watch id prop for global modal usage
unwatch.push(watch(() => props.id, async (newId) => {
    if (newId !== null && newId !== undefined) {
        await loadRecord();
    }
}));

const hasContext = !!(props.requestId || props.workOrderId || props.clientId || props.techId);
if (!props.embedded && props.id === null && !hasContext) {
    // Only watch route params if not in embedded mode, no id prop, and no context props
    unwatch.push(watch(() => route.params.id || route.name, async (nv, ov) => 
    {
        // If route name is "Part New" or id is undefined, treat as new
        let idValue = route.params.id || (route.name === "Part New" ? "new" : "");
        if (Array.isArray(idValue)) {
            idValue = idValue[0];
        }
        loadRecord(idValue);
    }, 
    { 
        immediate: true 
    }));
} else if (hasContext && props.id === null) {
    // Global modal with context props — create new record with pre-populated fields
    record.value = newRecord();
} else if (props.id !== null) {
    // Load immediately when id prop is provided
    loadRecord();
} else if (props.embedded) {
    // Load immediately in embedded mode
    loadRecord("new");
}

// If the user changes the Job inside PartForm, refresh the job header/title.
unwatch.push(watch(() => record.value?.RequestId, async (newId, oldId) => {
    if (newId && newId !== oldId) {
        await loadJobInfo();
    }
}));

onUnmounted(() => 
{
    unwatch.forEach((un) => 
    {
        un();
    });
});
    
</script>